// src/pages/public/TextileIndexPage.jsx
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { textileIndexApi } from '@/api/services'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { TrendingUp, TrendingDown, BarChart2, Search, AlertCircle, Package, Users, ShoppingBag, ArrowUpRight, ArrowDownRight } from 'lucide-react'

const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`
const COLORS = ['#FF3D7F', '#6366f1', '#22c55e', '#f59e0b', '#06b6d4', '#8b5cf6', '#ef4444', '#ec4899']

function StatCard({ icon: Icon, label, value, color = 'text-pink-500' }) {
  return (
    <div className="bg-white rounded-xl border p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-xl font-bold text-gray-900">{value}</p>
    </div>
  )
}

function ChangeIndicator({ value }) {
  if (value === null || value === undefined) return <span className="text-xs text-gray-400">—</span>
  const isPositive = value > 0
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
      {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {Math.abs(value)}%
    </span>
  )
}

function CategoryTrendChart({ categoryId }) {
  const { data, isLoading } = useQuery({
    queryKey: ['textile-trend', categoryId],
    queryFn: () => textileIndexApi.categoryTrend(categoryId, { days: 90 }).then(r => r.data),
    enabled: !!categoryId,
  })

  if (!categoryId) return null
  if (isLoading) return <div className="text-center py-8 text-gray-400 text-sm">Loading trend...</div>

  const trend = data?.data?.trend || []
  if (trend.length === 0) return <p className="text-sm text-gray-400 text-center py-6">No trend data available</p>

  return (
    <div>
      <h3 className="font-bold text-sm text-gray-700 mb-3">{data?.data?.category?.name} — 90-Day Price Trend</h3>
      <ResponsiveContainer width="100%" height={250}>
        <LineChart data={trend}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="snapshot_date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
          <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `₹${v}`} />
          <Tooltip formatter={v => fmt(v)} labelFormatter={l => `Date: ${l}`} />
          <Line type="monotone" dataKey="avg_price" stroke="#FF3D7F" strokeWidth={2} dot={false} name="Avg Price" />
          <Line type="monotone" dataKey="min_price" stroke="#22c55e" strokeWidth={1} dot={false} name="Min" strokeDasharray="4 4" />
          <Line type="monotone" dataKey="max_price" stroke="#ef4444" strokeWidth={1} dot={false} name="Max" strokeDasharray="4 4" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

export default function TextileIndexPage() {
  const [selectedCategory, setSelectedCategory] = useState(null)

  const { data, isLoading } = useQuery({
    queryKey: ['textile-index'],
    queryFn: () => textileIndexApi.index().then(r => r.data),
  })

  if (isLoading) return <div className="text-center py-16 text-gray-400">Loading Textile Index...</div>

  const idx = data?.data || {}
  const summary = idx.summary || {}
  const priceIndices = idx.price_indices || []
  const trendingFabrics = idx.trending_fabrics || []
  const demandSignals = idx.demand_signals || {}
  const marketGaps = idx.market_gaps || []
  const monthlyTrend = idx.monthly_trend || []

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-10">
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-pink-900 rounded-2xl p-8 md:p-12 text-white">
        <div className="flex items-center gap-2 mb-2">
          <BarChart2 className="w-6 h-6 text-pink-400" />
          <span className="text-xs font-bold uppercase tracking-widest text-pink-300">Monthly Report</span>
        </div>
        <h1 className="text-3xl md:text-4xl font-extrabold mb-2">Fabrinza Textile Index</h1>
        <p className="text-gray-300 max-w-2xl text-sm md:text-base mb-4">
          India's most comprehensive real-time textile market intelligence. Track fabric prices, demand trends,
          and market signals across categories — updated daily from live marketplace data.
        </p>
        <p className="text-xs text-gray-400">
          Last updated: {idx.generated_at ? new Date(idx.generated_at).toLocaleDateString('en-IN', { dateStyle: 'long' }) : '—'}
        </p>
      </div>

      {/* Platform stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={Package} label="Active Products" value={summary.active_products?.toLocaleString()} />
        <StatCard icon={Users} label="Active Suppliers" value={summary.active_suppliers?.toLocaleString()} color="text-blue-500" />
        <StatCard icon={BarChart2} label="Categories" value={summary.categories} color="text-purple-500" />
        <StatCard icon={ShoppingBag} label="Monthly Orders" value={summary.monthly_orders?.toLocaleString()} color="text-green-500" />
        <StatCard icon={TrendingUp} label="Avg Order Value" value={fmt(summary.avg_order_value)} color="text-amber-500" />
      </div>

      {/* Price Index Table */}
      <section className="bg-white rounded-xl border">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="font-bold text-gray-900">Category Price Index</h2>
            <p className="text-xs text-gray-500">Current average prices by fabric category with 30-day change</p>
          </div>
        </div>
        {priceIndices.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No price data yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-600">
                  <th className="px-4 py-3 font-medium">Category</th>
                  <th className="px-4 py-3 font-medium text-right">Avg Price</th>
                  <th className="px-4 py-3 font-medium text-right">Min</th>
                  <th className="px-4 py-3 font-medium text-right">Max</th>
                  <th className="px-4 py-3 font-medium text-right">Median</th>
                  <th className="px-4 py-3 font-medium text-center">Listings</th>
                  <th className="px-4 py-3 font-medium text-center">30d Change</th>
                  <th className="px-4 py-3 font-medium text-center">Trend</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {priceIndices.map((idx, i) => (
                  <tr key={idx.category_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{idx.category}</td>
                    <td className="px-4 py-3 text-right font-bold">{fmt(idx.avg_price)}</td>
                    <td className="px-4 py-3 text-right text-green-600">{fmt(idx.min_price)}</td>
                    <td className="px-4 py-3 text-right text-red-500">{fmt(idx.max_price)}</td>
                    <td className="px-4 py-3 text-right">{fmt(idx.median_price)}</td>
                    <td className="px-4 py-3 text-center text-gray-500">{idx.sample_count}</td>
                    <td className="px-4 py-3 text-center"><ChangeIndicator value={idx.change_30d} /></td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => setSelectedCategory(idx.category_id === selectedCategory ? null : idx.category_id)}
                        className="text-xs text-pink-500 hover:text-pink-700 font-medium">
                        {selectedCategory === idx.category_id ? 'Hide' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Category trend chart (expandable) */}
      {selectedCategory && (
        <div className="bg-white rounded-xl border p-5">
          <CategoryTrendChart categoryId={selectedCategory} />
        </div>
      )}

      {/* Monthly GMV Trend */}
      {monthlyTrend.length > 0 && (
        <section className="bg-white rounded-xl border p-5">
          <h2 className="font-bold text-gray-900 mb-1">Platform Growth — Monthly GMV</h2>
          <p className="text-xs text-gray-500 mb-4">Total marketplace Gross Merchandise Value over the last 12 months</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={monthlyTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `₹${(v / 100000).toFixed(0)}L`} />
              <Tooltip formatter={v => fmt(v)} />
              <Bar dataKey="gmv" fill="#FF3D7F" radius={[4, 4, 0, 0]} name="GMV" />
            </BarChart>
          </ResponsiveContainer>
        </section>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Trending Fabrics */}
        <section className="bg-white rounded-xl border p-5">
          <h2 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-green-500" /> Trending Fabric Types
          </h2>
          <p className="text-xs text-gray-500 mb-4">Top-selling fabric types in the last 30 days</p>
          {trendingFabrics.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No data</p>
          ) : (
            <div className="space-y-2.5">
              {trendingFabrics.slice(0, 10).map((f, i) => {
                const maxUnits = trendingFabrics[0]?.total_units || 1
                const pct = Math.round((f.total_units / maxUnits) * 100)
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between text-sm mb-0.5">
                      <span className="font-medium text-gray-800">{f.fabric_type}</span>
                      <span className="text-xs text-gray-500">{f.total_units} units · {f.order_count} orders · Avg {fmt(f.avg_price)}/m</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </section>

        {/* Demand Signals */}
        <section className="bg-white rounded-xl border p-5">
          <h2 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
            <Search className="w-4 h-4 text-blue-500" /> Demand Signals
          </h2>
          <p className="text-xs text-gray-500 mb-4">What buyers are searching for right now</p>

          {/* Top searches */}
          <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">Top Searches (14 days)</h3>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {(demandSignals.top_searches || []).slice(0, 12).map((s, i) => (
              <span key={i} className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                {s.query} <span className="text-blue-400">({s.count})</span>
              </span>
            ))}
          </div>

          {/* Rising searches */}
          {(demandSignals.rising_searches || []).length > 0 && (
            <>
              <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                <TrendingUp className="w-3 h-3 text-green-500" /> Rising Searches
              </h3>
              <div className="space-y-1.5">
                {demandSignals.rising_searches.slice(0, 6).map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-gray-800">{s.query}</span>
                    <span className="text-xs font-bold text-green-600">+{s.growth_pct}%</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      </div>

      {/* Market Gaps */}
      {marketGaps.length > 0 && (
        <section className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <h2 className="font-bold text-amber-900 mb-1 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> Market Gaps — Unmet Demand
          </h2>
          <p className="text-xs text-amber-700 mb-3">These searches returned zero results — potential opportunities for suppliers</p>
          <div className="flex flex-wrap gap-2">
            {marketGaps.slice(0, 15).map((g, i) => (
              <span key={i} className="px-3 py-1.5 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                {g.query} <span className="text-amber-500">({g.count}x)</span>
              </span>
            ))}
          </div>
        </section>
      )}

      {/* API callout */}
      <section className="bg-gray-900 rounded-xl p-6 md:p-8 text-white">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-pink-500/20 flex items-center justify-center flex-shrink-0">
            <BarChart2 className="w-6 h-6 text-pink-400" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Fabrinza Trend API</h3>
            <p className="text-sm text-gray-300 mt-1 max-w-xl">
              Access real-time textile market data programmatically. Our API provides price indices,
              demand signals, trending fabrics, and market gap analysis — ideal for fashion brands,
              commodity traders, and industry analysts.
            </p>
            <div className="mt-4 flex items-center gap-3">
              <code className="text-xs bg-gray-800 px-3 py-1.5 rounded-lg font-mono text-pink-300">
                GET /api/textile-index/api
              </code>
              <span className="text-xs text-gray-400">JSON format · Updated daily · Free tier available</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer attribution */}
      <p className="text-center text-xs text-gray-400">
        The Fabrinza Textile Index is compiled from live marketplace data across {summary.active_suppliers || 0}+ verified suppliers
        and {summary.active_products || 0}+ fabric listings. Data is refreshed daily. For licensing enquiries, contact data@fabrinza.com.
      </p>
    </div>
  )
}
